import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import type { Permission } from '@lightbm/shared';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { ForbiddenPage } from '@/components/feedback/ForbiddenPage';
import { useAuth } from './auth-context';

/** Only same-app paths are accepted as a "next" destination (no open redirect). */
export function safeNextPath(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\'))
    return '/';
  return value;
}

/** Signed-in users only. Users who must change their password are sent there first. */
export function RequireAuth() {
  const { user, isLoading, sessionEnded } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;
  if (!user) {
    const params = new URLSearchParams();
    const next = `${location.pathname}${location.search}`;
    if (next !== '/') params.set('next', next);
    if (sessionEnded) params.set('reason', 'expired');
    const query = params.toString();
    return <Navigate to={`/sign-in${query ? `?${query}` : ''}`} replace />;
  }
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}

/** Sign-in and password recovery pages: a signed-in user goes straight to the app. */
export function GuestOnly() {
  const { user, isLoading } = useAuth();
  const [params] = useSearchParams();
  if (isLoading) return <FullPageSpinner />;
  if (user) return <Navigate to={safeNextPath(params.get('next'))} replace />;
  return <Outlet />;
}

/** The page shows only with at least one of the permissions (the server checks again). */
export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: Permission[];
  children?: ReactNode;
}) {
  const { canAny } = useAuth();
  if (!canAny(...anyOf)) return <ForbiddenPage />;
  return children ?? <Outlet />;
}
